import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Info, Lock, Loader2, X } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import CachedImg from '../components/CachedImg';

const PLANNING_VIDEO_URL = 'https://ai-agency-dt7d.onrender.com/webhook/planning_video';

type FormatoKey = 'post' | 'story' | 'square';
type DurationMode = 'unica' | 'multiples';

const formatoOptions: Array<{ id: FormatoKey; name: string; image: string }> = [
  {
    id: 'post',
    name: 'Posteo (4:5)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Posteo.jpeg',
  },
  {
    id: 'story',
    name: 'Historias/Reels (9:16)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Historia.jpeg',
  },
  {
    id: 'square',
    name: 'Cuadrado (1:1)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Cuadrado.jpeg',
  },
];

const durationModeOptions: Array<{ id: DurationMode; name: string; description: string; materialIcon: string }> = [
  {
    id: 'unica',
    name: 'Escena única',
    description: 'Video corto de aproximadamente 10 segundos, ideal para una sola toma cinematográfica.',
    materialIcon: 'movie',
  },
  {
    id: 'multiples',
    name: 'Múltiples escenas',
    description: 'Video más largo y elaborado con varias escenas y transiciones.',
    materialIcon: 'movie_filter',
  },
];

const ConfigurarVideoCinematografico: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );

  const [prompt, setPrompt] = useState<string>('');
  const [durationMode, setDurationMode] = useState<DurationMode>('unica');
  const [modoPro, setModoPro] = useState<boolean>(false);
  const [formato, setFormato] = useState<FormatoKey>('story');

  // Plan state for Modo Pro
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [showProPlanModal, setShowProPlanModal] = useState<boolean>(false);
  const isProPlanAllowed = userPlan === 'negocio' || userPlan === 'enterprise';

  // Menu states - Formato
  const [showFormatoMenu, setShowFormatoMenu] = useState<boolean>(false);
  const [formatoMenuAnimated, setFormatoMenuAnimated] = useState<boolean>(false);
  const [formatoMenuDragY, setFormatoMenuDragY] = useState<number>(0);
  const formatoDragStartY = useRef<number | null>(null);

  // Menu states - Duration mode
  const [showDurationMenu, setShowDurationMenu] = useState<boolean>(false);
  const [durationMenuAnimated, setDurationMenuAnimated] = useState<boolean>(false);
  const [durationMenuDragY, setDurationMenuDragY] = useState<number>(0);
  const durationDragStartY = useRef<number | null>(null);

  // Planning state
  const [planning, setPlanning] = useState<boolean>(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const planningLockRef = useRef<boolean>(false);

  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  const canPlan = prompt.trim().length > 0 && !planning;

  // Set mobile header title
  useEffect(() => {
    setMobileTitleOverride('Video cinematográfico');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  // Fetch user plan for Modo Pro check
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoadingPlan(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) { setLoadingPlan(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', profile.id_company)
          .single();
        if (company?.plan) {
          setUserPlan(company.plan);
          if (company.plan !== 'negocio' && company.plan !== 'enterprise') {
            setModoPro(false);
          }
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchUserPlan();
  }, []);

  // Trigger animations after menu mounts
  useEffect(() => {
    if (showFormatoMenu) {
      requestAnimationFrame(() => setFormatoMenuAnimated(true));
    }
  }, [showFormatoMenu]);

  useEffect(() => {
    if (showDurationMenu) {
      requestAnimationFrame(() => setDurationMenuAnimated(true));
    }
  }, [showDurationMenu]);

  // Close menu helpers
  const closeFormatoMenu = () => {
    setFormatoMenuAnimated(false);
    setTimeout(() => setShowFormatoMenu(false), 300);
  };
  const closeDurationMenu = () => {
    setDurationMenuAnimated(false);
    setTimeout(() => setShowDurationMenu(false), 300);
  };

  // Drag handlers - Formato
  const handleFormatoTouchStart = (e: React.TouchEvent) => {
    formatoDragStartY.current = e.touches[0].clientY;
  };
  const handleFormatoTouchMove = (e: React.TouchEvent) => {
    if (formatoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - formatoDragStartY.current;
    if (deltaY > 0) setFormatoMenuDragY(deltaY);
  };
  const handleFormatoTouchEnd = () => {
    if (formatoMenuDragY > 100) {
      closeFormatoMenu();
    }
    setFormatoMenuDragY(0);
    formatoDragStartY.current = null;
  };

  // Drag handlers - Duration mode
  const handleDurationTouchStart = (e: React.TouchEvent) => {
    durationDragStartY.current = e.touches[0].clientY;
  };
  const handleDurationTouchMove = (e: React.TouchEvent) => {
    if (durationDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - durationDragStartY.current;
    if (deltaY > 0) setDurationMenuDragY(deltaY);
  };
  const handleDurationTouchEnd = () => {
    if (durationMenuDragY > 100) {
      closeDurationMenu();
    }
    setDurationMenuDragY(0);
    durationDragStartY.current = null;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePlanVideo = async () => {
    if (planningLockRef.current) return;
    planningLockRef.current = true;

    if (!prompt.trim() || planning) {
      planningLockRef.current = false;
      return;
    }

    try {
      setPlanError(null);
      setPlanning(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const variantIds = ids.map(s => String(s).trim()).filter(s => !!s && s !== '0');

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds.length > 0 ? variantIds : undefined,
        tipo_video: 'cinematografico',
        prompt: prompt.trim(),
        duracion_mode: durationMode,
        Format: formato === 'story' ? '9:16' : formato === 'square' ? '1:1' : '4:5',
        modo_pro: modoPro,
      };

      console.log('[ConfigurarVideoCinematografico] handlePlanVideo payload', requestData);

      const response = await fetch(PLANNING_VIDEO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.clone().text(); } catch {}
      console.log('[ConfigurarVideoCinematografico] response', { status: response.status, ok: response.ok, text: respText });

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[ConfigurarVideoCinematografico] json parse error', e); }

      if (data?.response?.scenes) {
        // Navigate to planner with scene data
        navigate('/redes-sociales/crear-anuncio/planificador-video-cinematografico', {
          state: {
            planData: data.response,
            prompt: prompt.trim(),
            durationMode,
            formato,
            modoPro,
            ids: variantIds,
          },
        });
      } else {
        throw new Error('No se recibieron las escenas del video.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoCinematografico] handlePlanVideo error', e);
      setPlanError('Se produjo un error al planificar el video. Por favor, volvé a intentarlo.');
    } finally {
      setPlanning(false);
      planningLockRef.current = false;
    }
  };

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove('tap-flash');
    void el.offsetWidth;
    el.classList.add('tap-flash');
  };

  return (
    <div className="min-h-[100dvh] md:min-h-screen bg-[#F2F2F7]">
      {/* Desktop sticky header */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-4xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Video cinematográfico</h1>
              <button
                type="button"
                onClick={handlePlanVideo}
                disabled={!canPlan}
                className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-[10px] text-[15px] font-medium transition-all ${
                  !canPlan
                    ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                    : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
                }`}
              >
                {planning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Planificando...
                  </>
                ) : 'Planear video'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-1 pb-28 sm:px-8 sm:pt-[72px] sm:pb-6">

        {/* Prompt — iOS grouped list */}
        <div className="mt-2 sm:mt-4">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Descripción del video</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
            <div className="px-4 py-3">
              <textarea
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ej: Un video cinematográfico mostrando el producto sobre una mesa de mármol, con iluminación suave y movimiento de cámara lento hacia adelante..."
                className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
              />
            </div>
          </div>
          <p className="text-[13px] text-[#3C3C43]/60 mt-2 px-4">Describí el estilo, la atmósfera y los movimientos de cámara que imaginás.</p>
        </div>

        {/* Configuración — iOS grouped list */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Configuración</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">

            {/* Tipo de duración */}
            <button
              type="button"
              onClick={() => { closeFormatoMenu(); setShowDurationMenu(true); }}
              onClickCapture={tapFlash}
              className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">theaters</span>
                <span className="text-[15px] text-brand-black/90">Tipo de video</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="text-[15px] text-[#3C3C43]/60">{durationModeOptions.find(o => o.id === durationMode)?.name || 'Escena única'}</span>
                <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
              </span>
            </button>

            <div className="ml-[46px] h-px bg-black/[0.08]" />

            {/* Formato */}
            <button
              type="button"
              onClick={() => { closeDurationMenu(); setShowFormatoMenu(true); }}
              onClickCapture={tapFlash}
              className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">aspect_ratio</span>
                <span className="text-[15px] text-brand-black/90">Formato</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="text-[15px] text-[#3C3C43]/60">{formatoOptions.find(o => o.id === formato)?.name || 'Historias/Reels (9:16)'}</span>
                <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
              </span>
            </button>
          </div>
        </div>

        {/* Calidad de video — Modo Pro toggle */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Calidad de video</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)] relative">
            <div className="px-4 py-[11px] flex items-center justify-between">
              <span className="flex items-center gap-3 flex-1 min-w-0">
                <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">high_quality</span>
                <span className="text-left min-w-0">
                  <span className="text-[15px] text-brand-black/90 flex items-center gap-2">
                    Modo Pro
                    {!loadingPlan && !isProPlanAllowed && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-black/[0.05] text-[#3C3C43]/40">
                        <Lock className="w-3 h-3" />
                        Plan Pro / Ultra
                      </span>
                    )}
                  </span>
                  <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">Mayor calidad y definición</span>
                </span>
              </span>
              <label className="relative inline-flex cursor-pointer shrink-0 ml-3">
                <input
                  type="checkbox"
                  checked={modoPro && isProPlanAllowed}
                  onChange={() => {
                    if (isProPlanAllowed) {
                      setModoPro(!modoPro);
                    } else if (!loadingPlan) {
                      setShowProPlanModal(true);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className={`w-[51px] h-[31px] rounded-full transition-colors ${modoPro && isProPlanAllowed ? 'bg-[#1644EB]' : 'bg-black/[0.09]'}`} />
                <div className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform ${modoPro && isProPlanAllowed ? 'translate-x-[20px]' : 'translate-x-0'}`} />
              </label>
            </div>
            {isProPlanAllowed && modoPro && (
              <>
                <div className="ml-[46px] h-px bg-black/[0.08]" />
                <div className="px-4 py-[11px] flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                  <span className="text-[13px] text-[#3C3C43]/60">El Modo Pro consume más imágenes por escena.</span>
                </div>
              </>
            )}
          </div>

          {/* Pro Plan Popover */}
          {showProPlanModal && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowProPlanModal(false)} />
              <div className="relative z-50 mt-2 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-[8px] bg-black/[0.05] flex items-center justify-center">
                    <Lock className="w-[18px] h-[18px] text-[#3C3C43]/40" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[15px] font-semibold text-brand-black/90">Función exclusiva</h4>
                    <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                      El Modo Pro está disponible solo para los planes Pro y Ultra.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowProPlanModal(false); navigate('/subscription'); }}
                  className="mt-4 w-full text-[15px] font-semibold text-[#1644EB] py-2 text-center hover:opacity-70 transition-opacity"
                >
                  Ver planes
                </button>
              </div>
            </>
          )}
        </div>

      </div>

      {/* Mobile bottom bar — frosted glass */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <button
          type="button"
          onClick={handlePlanVideo}
          disabled={!canPlan}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            !canPlan ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed' : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {planning ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Planificando...
            </span>
          ) : 'Planear video'}
        </button>
      </div>

      {/* Desktop backdrop overlay when side panel is open */}
      {(showDurationMenu || showFormatoMenu) && (
        <div
          className="hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer"
          onClick={() => { closeDurationMenu(); closeFormatoMenu(); }}
        />
      )}

      {/* Duration Mode Menu */}
      {showDurationMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              durationMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Tipo de video</h3>
              <button type="button" onClick={closeDurationMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-1">
                {durationModeOptions.map((opt) => {
                  const selected = durationMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setDurationMode(opt.id); closeDurationMenu(); }}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${
                        selected ? 'bg-neutral-100' : ''
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                        selected ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        <span className="material-symbols-rounded text-[18px]">{opt.materialIcon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">{opt.name}</p>
                        <p className={`text-[13px] ${selected ? 'text-neutral-600' : 'text-neutral-400'}`}>{opt.description}</p>
                      </div>
                      {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closeDurationMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              <button type="button" onClick={closeDurationMenu} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">Aceptar</button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${durationMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
                onClick={closeDurationMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: 'calc(100dvh - 1rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: durationMenuDragY > 0
                    ? `translateY(${durationMenuDragY}px)`
                    : durationMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: durationMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleDurationTouchStart}
                  onTouchMove={handleDurationTouchMove}
                  onTouchEnd={handleDurationTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closeDurationMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Tipo de video</h3>
                  <button type="button" onClick={closeDurationMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="space-y-1">
                    {durationModeOptions.map((opt) => {
                      const selected = durationMode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => { setDurationMode(opt.id); closeDurationMenu(); }}
                          className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${
                            selected ? 'bg-neutral-100' : ''
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                            selected ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'
                          }`}>
                            <span className="material-symbols-rounded text-[18px]">{opt.materialIcon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-medium text-neutral-900">{opt.name}</p>
                            <p className={`text-[13px] ${selected ? 'text-neutral-600' : 'text-neutral-400'}`}>{opt.description}</p>
                          </div>
                          {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
        </>
      )}

      {/* Formato Menu */}
      {showFormatoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              formatoMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Formato</h3>
              <button type="button" onClick={closeFormatoMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                {formatoOptions.map(opt => {
                  const selected = formato === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setFormato(opt.id); closeFormatoMenu(); }}
                      className={`w-full rounded-[13px] p-3 transition-colors focus:outline-none flex items-center gap-4 ${
                        selected
                          ? 'bg-neutral-100 ring-2 ring-[#1644EB]'
                          : 'bg-neutral-50 hover:bg-neutral-100'
                      }`}
                    >
                      <div className="relative overflow-hidden rounded-[10px] w-20 h-20 shrink-0">
                        <CachedImg src={opt.image} alt={opt.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <p className="text-[15px] text-neutral-900 font-medium">{opt.name}</p>
                        {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closeFormatoMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              <button type="button" onClick={closeFormatoMenu} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">Aceptar</button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${formatoMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
                onClick={closeFormatoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: 'calc(100dvh - 1rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: formatoMenuDragY > 0
                    ? `translateY(${formatoMenuDragY}px)`
                    : formatoMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: formatoMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleFormatoTouchStart}
                  onTouchMove={handleFormatoTouchMove}
                  onTouchEnd={handleFormatoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closeFormatoMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Formato</h3>
                  <button type="button" onClick={closeFormatoMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    {formatoOptions.map(opt => {
                      const selected = formato === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => { setFormato(opt.id); closeFormatoMenu(); }}
                          className={`relative rounded-[13px] overflow-hidden transition-all ${
                            selected ? 'ring-2 ring-[#1644EB]' : 'ring-1 ring-black/[0.06] active:bg-black/[0.02]'
                          }`}
                        >
                          <div className="bg-white px-4 pt-4">
                            <CachedImg src={opt.image} alt={opt.name} className="w-full h-40 object-cover rounded-[10px]" />
                          </div>
                          {selected && (
                            <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <p className="text-[13px] text-brand-black/90 px-3 py-2.5 font-medium text-center bg-white">{opt.name}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
        </>
      )}

      {/* Error message */}
      {planError && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-red-50 border border-red-200 rounded-[13px] p-4 z-50">
          <p className="text-[15px] font-medium text-red-600">Error al planificar el video</p>
          <p className="text-[13px] text-red-600 mt-1">{planError}</p>
        </div>
      )}
    </div>
  );
};

export default ConfigurarVideoCinematografico;
